import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useErrorLogService } from 'ErrorLogging';
import { navigationHelper } from 'NavigationHelpers';
import { useSendPing } from 'Status';
import { showToast } from 'Toast';
import { stripeCheckoutSessionApi } from './StripeCheckoutSessionApi';
import { type StripeCheckoutSessionParams } from './TuitionAndRegistration.types';
import { LOCALE_PREFIX } from './constants';

// We do not allow registration on Cordova. The learner goes to a system browser and registers there.
function openApplicationStatusInBrowser() {
    navigationHelper().loadUrl(`${window.ENDPOINT_ROOT}/settings/application_status`, '_blank');
}

type UseStripeCheckoutSessionResult = {
    // True while we wait on Stripe. Disables the register button.
    processingRegistration: boolean;
    // True on Cordova, where the register button becomes "launch browser" instead.
    registerRequiresRedirectToBrowser: boolean;
    openStripeCheckoutSessionOrBrowser: (params: StripeCheckoutSessionParams) => Promise<void>;
};

export function useStripeCheckoutSession(): UseStripeCheckoutSessionResult {
    const { t } = useTranslation('back_royal');
    const ErrorLogService = useErrorLogService();
    const { sendPing } = useSendPing();
    const [createCheckoutSession] = stripeCheckoutSessionApi.useCreateCheckoutSessionMutation();
    const [processingRegistration, setProcessingRegistration] = useState(false);

    const registerRequiresRedirectToBrowser = !!window.CORDOVA;

    const openStripeCheckoutSession = useCallback(
        async (params: StripeCheckoutSessionParams) => {
            setProcessingRegistration(true);

            try {
                const response = await createCheckoutSession(params).unwrap();
                const url = response?.session?.url;

                /*
                    The server always creates a fresh session, and an open session always carries a
                    URL, so this should not happen. The already-registered case arrives as a 409
                    below. Keep the old no-op rather than a toast, and log so we learn if it occurs.
                */
                if (!url) {
                    ErrorLogService.notifyInProd('Stripe checkout session came back with no URL', null, {
                        level: 'warning',
                        admission_offer_id: params.admission_offer_id,
                    });
                    setProcessingRegistration(false);
                    return;
                }

                window.location.href = url;
            } catch (error) {
                /*
                    409 means an active subscription already exists for this admission offer: the
                    learner completed checkout and this client has not caught up. Ping the server so
                    currentUser resyncs (CurrentUserInterceptor merges push_messages.current_user in),
                    which moves them off this screen. No toast -- nothing went wrong for the learner.
                */
                if ((error as { status?: number })?.status === 409) {
                    await sendPing();
                    setProcessingRegistration(false);
                    return;
                }

                setProcessingRegistration(false);
                showToast({ message: t(`${LOCALE_PREFIX}.registration_failed`), type: 'danger' });
                ErrorLogService.notify('Failed to create a Stripe checkout session', error);
            }
        },
        [createCheckoutSession, sendPing, t, ErrorLogService],
    );

    return {
        processingRegistration,
        registerRequiresRedirectToBrowser,
        openStripeCheckoutSessionOrBrowser: registerRequiresRedirectToBrowser
            ? async () => openApplicationStatusInBrowser()
            : openStripeCheckoutSession,
    };
}
